import { ChevronRight } from '@mui/icons-material';
import { DatePicker } from '@mui/lab';
import DateAdapter from '@mui/lab/AdapterDateFns';
import LocalizationProvider from '@mui/lab/LocalizationProvider';
import {
  Autocomplete,
  Box,
  Button,
  Grid,
  Stack,
  styled,
  TextField,
  CircularProgress,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { Controller, FormProvider, useForm, useFormContext } from 'react-hook-form';
import {
  createNewFeeArrangement,
  FeeArrangementDTO,
  getClientContactAutocompleteOptions,
  getCurrencyList,
  updateFeeArrangement,
  LogEntityType,
  saveActivityLog,
  LogContextType,
  LogAction,
  getFeeArrangementById,
  ClientContactDTO,
  getClientContactById,
} from 'wisestep-api';
import {
  ControlledTextArea,
  ControlledTextInput,
  Label,
  WsButton,
  WsColors,
  WsDatePicker,
  WsSelect,
  WsToggleButton,
} from 'wisestep-base';

import {
  getAvEditAddDesUiName,
  activityLogDTOHelper,
} from '../../../../features/activityLog/ActivityHelper';
import { useFetch } from '../../../../hooks/useFetch';
import useWsAutocomplete from '../../../../hooks/useWsAutocomplete';
import ErrorLogger from '../../../../providers/ErrorLogger';
import { getCurrencySymbol } from '../../../../utils';
import { failed, success, warning } from '../../../../utils/NotificationMessages';
import { useNotificationStore } from '../../../../utils/NotificationStore';
import { FormProps } from '../../../clientsSettings/components/FileCategoryForm';
import { getAutocompleteLabelForClientContact } from '../../../jobs/helper';

export const getFeeArrangementFees = (feeArr?: FeeArrangementDTO) => {
  return feeArr
    ? `${
        feeArr.feeType === 'PERCENTAGE' || !feeArr.currency
          ? ''
          : getCurrencySymbol(feeArr.currency)
      }${feeArr.fee}${feeArr.feeType === 'PERCENTAGE' ? '%' : ''}`
    : '';
};

interface AutocompleteWithPersistentAddProps<T> {
  name: string;
  options: T[];
  label?: string;
  onCreate?(item): void;
  optionLabelKey?: string;
  creatable?: boolean;
  persistentAddOption?: boolean;
  required?: boolean;
  placeholder?: string;
  multiple?: boolean;
  disabled?: boolean;
  getOptionLabel?(option: T): string;
  changed?(value: T | T[] | null): void;
}

export const AutocompleteWithPersistentAdd = <T,>({
  name,
  options,
  onCreate,
  optionLabelKey = 'label',
  creatable = false,
  persistentAddOption = false,
  label,
  required = false,
  placeholder = 'Search option...',
  multiple = false,
  disabled = false,
  getOptionLabel,
  changed,
}: AutocompleteWithPersistentAddProps<T>) => {
  const form = useFormContext();
  const autocompleteProps = useWsAutocomplete(
    getOptionLabel ?? optionLabelKey,
    creatable,
    persistentAddOption,
    label ?? placeholder
  );

  return (
    <>
      {label && <Label>{label}</Label>}
      <Controller
        control={form.control}
        name={name}
        render={({ field: { onChange, value: fieldValue } }) => (
          <Autocomplete
            value={fieldValue}
            size="small"
            filterOptions={autocompleteProps.filterOptions}
            selectOnFocus
            clearOnBlur
            handleHomeEndKeys
            multiple={multiple}
            options={options}
            disabled={disabled}
            getOptionLabel={getOptionLabel ?? autocompleteProps.getOptionLabel}
            onChange={(event, value) => {
              const item = multiple ? (value as []).at(-1) : (value as Record<string, unknown>);
              if (item?.inputValue || item?.inputValue === '') {
                onCreate?.(item);
              } else {
                onChange(value);
                changed?.(value);
              }
            }}
            renderOption={autocompleteProps.renderOption}
            renderInput={(params) => (
              <TextField {...params} label={placeholder} {...(required && { required })} />
            )}
          />
        )}
      />
    </>
  );
};

const DrawerFooter = styled(Grid)(() => ({
  position: 'fixed',
  bottom: 1,
  justifyContent: 'revert',
  background: WsColors.TertiaryShades100,
  width: '50vw',
}));

const contractTypes = [
  {
    label: 'Permanent',
    value: 'PERMANENT',
  },
  {
    label: 'Contract',
    value: 'CONTRACT',
  },
  {
    label: 'Temporary',
    value: 'TEMPORARY',
  },
  {
    label: 'FullTime',
    value: 'FULLTIME',
  },
  {
    label: 'Internship',
    value: 'INTERNSHIP',
  },
  {
    label: 'ContractToHireW2',
    value: 'CONTACTTOHIREW2',
  },
  {
    label: 'ContractW2',
    value: 'CONTRACTW2',
  },
  {
    label: 'ContractIndependent',
    value: 'CONTRACTINDEPENDENT',
  },
  {
    label: 'ContractToHireIndependent',
    value: 'CONTRACTTOHIREINDEPENDENT',
  },
  {
    label: 'ContractCorpToCorp',
    value: 'CONTARCTCORPTOCORP',
  },
  {
    label: 'ContractToHireCorpToCorp',
    value: 'CONTARCTTOHIRECORPTOCORP',
  },
  {
    label: 'PartTime',
    value: 'PARTTIME',
  },
  {
    label: 'Freelance',
    value: 'FREELANCE',
  },
  {
    label: 'ThirdParty',
    value: 'THIRDPARTY',
  },
];

const feeTypes: { label: string; value: 'PERCENTAGE' | 'FIXED' }[] = [
  {
    label: 'Percentage',
    value: 'PERCENTAGE',
  },
  {
    label: 'Fixed',
    value: 'FIXED',
  },
];

const contractfeeTypes = [
  {
    label: 'Percentage',
    value: 'PERCENTAGE',
  },
  {
    label: 'Fixed',
    value: 'FIXED',
  },
  {
    label: 'Transactional',
    value: 'TRANSACTIONAL',
  },
];

const serviceFeePeriod = [
  {
    label: 'Hourly',
    value: 'HOURLY',
  },
  {
    label: 'Daily',
    value: 'DAILY',
  },
  {
    label: 'Weekly',
    value: 'WEEKLY',
  },
  {
    label: 'Monthly',
    value: 'MONTHLY',
  },
  {
    label: 'Annually',
    value: 'ANNUALLY',
  },
  {
    label: 'Once',
    value: 'ONCE',
  },
];

interface IAddEditFeeArrangement<T> extends FormProps<T> {
  clientId: string;
  clientName?: string;
  id?: string;
  modalType?: string;
}

const feeArrangementDefaultValues = {
  contractType: 'PERMANENT',
  feeArrangementLevel: '',
  feeType: 'FIXED',
  serviceFeePeriod: '',
  fee: '',
} as unknown as FeeArrangementDTO;

const FeeArrangementForm = ({
  clientId,
  onCancel,
  onSave,
  id,
  modalType,
}: IAddEditFeeArrangement<FeeArrangementDTO>) => {
  const methods = useForm<FeeArrangementDTO>({
    defaultValues: { ...feeArrangementDefaultValues, id, clientId } as FeeArrangementDTO,
  });
  const {
    reset,
    formState: { dirtyFields },
    watch,
    setValue,
    unregister,
  } = methods;
  const { setNotification } = useNotificationStore();
  const [clientContactOptions, setClientContactOptions] = useState<ClientContactDTO[]>([]);
  const feeType = watch('feeType');
  const startDate = watch('startDate');
  const endDate = watch('endDate');
  const arrangementType = watch('contractType');
  const [currencies] = useFetch(getCurrencyList);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (feeType !== 'FIXED') {
      unregister('currency');
    }
  }, [feeType, unregister]);

  useEffect(() => {
    (async () => {
      if (!id) return;
      const feeArrangement: FeeArrangementDTO = (await getFeeArrangementById(id)).data;
      if (feeArrangement.clientContactId) {
        const clientContact: ClientContactDTO = (
          await getClientContactById(feeArrangement.clientContactId)
        ).data.data;
        setClientContactOptions([clientContact]);
      }
      reset(feeArrangement);
    })();
  }, [id, reset]);

  const handleSave = async (data: FeeArrangementDTO) => {
    const arrangement: FeeArrangementDTO = {
      ...data,
      clientId,
    };
    setIsLoading(true);
    try {
      let updatedArrangement: FeeArrangementDTO;
      const avPayload = { ...activityLogDTOHelper };
      const avChangedValueViaDirtyFileds = Object.keys(dirtyFields).toString().split(',');
      if (id) {
        updatedArrangement = (await updateFeeArrangement(arrangement)).data;
        const clientContact = arrangement.clientContact.firstName
          ? `${arrangement.clientContact.firstName} in`
          : '';
        const avEmptyEntityfields = Object.keys(
          Object.entries(updatedArrangement).reduce((acc, [k, v]) => {
            if (v === '' || v === null) acc[k] = v;
            return acc;
          }, {})
        );
        avPayload.action = LogAction.Edit;
        avPayload.description = `${getAvEditAddDesUiName(
          'Client',
          avEmptyEntityfields,
          avChangedValueViaDirtyFileds
        )} for ${clientContact} under`;
        setNotification(success(`Contract has been saved successfully !!`));
      } else {
        updatedArrangement = (await createNewFeeArrangement(arrangement)).data;
        avPayload.action = LogAction.Add;
        avPayload.description = `ADDED new Fee Arrangement for`;
        setNotification(success(`Contract has been added successfully !!`));
      }
      onSave(updatedArrangement);
      avPayload.entityType = LogEntityType.Fee;
      avPayload.parentEntitiyType = LogContextType.Client;
      avPayload.parentEntitiyId = clientId;
      avPayload.entityId = updatedArrangement.id ?? '';
      avPayload.field = 'Fee Arrangements';
      avPayload.contextData = { clientId };
      avChangedValueViaDirtyFileds[0].length > 0 ? await saveActivityLog(avPayload) : '';
    } catch (error: any) {
      if (error.response.status === 400)
        setNotification(warning(`Oops, contract already exists!!`));
      else setNotification(failed('Something went wrong !!'));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <ErrorBoundary FallbackComponent={({ error }) => <ErrorLogger error={error} />}>
        <FormProvider {...methods}>
          <Box component="form" onSubmit={methods.handleSubmit(handleSave)}>
            <Grid container spacing={2} sx={{ px: 2, pt: 1, pb: 12 }}>
              <Grid item xs={12} sm={6}>
                <Label>Arrangement Name*</Label>
                <ControlledTextInput
                  required
                  placeholder="Arrangement Name"
                  name="feeArrangementLevel"
                  fullWidth
                  size="small"
                  inputProps={{}}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <WsSelect
                  required
                  label="Arrangement Type*"
                  name="contractType"
                  selectArray={contractTypes}
                  onChangeValue={(val) =>
                    setValue('feeType', val === 'PERMANENT' ? 'FIXED' : 'TRANSACTIONAL')
                  }
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Label>Client Contact Name</Label>
                <Controller
                  control={methods.control}
                  name="clientContactId"
                  render={({ field: { value, onChange } }) => (
                    <Autocomplete
                      value={clientContactOptions.find((option) => option.id === value) ?? null}
                      size="small"
                      selectOnFocus
                      clearOnBlur={false}
                      handleHomeEndKeys
                      options={clientContactOptions}
                      getOptionLabel={getAutocompleteLabelForClientContact}
                      onChange={(_event, item) => onChange(item?.id)}
                      isOptionEqualToValue={(option, selected) => option.id === selected.id}
                      onInputChange={async (_event, value) => {
                        const res = await getClientContactAutocompleteOptions(value, clientId);
                        const options = res.data;
                        options.forEach((option) => {
                          option.autocompleteLabel = getAutocompleteLabelForClientContact(option);
                          if (!option.id) {
                            option.id = option.value;
                          }
                        });
                        setClientContactOptions(options);
                      }}
                      renderInput={(params) => (
                        <TextField {...params} label="Search client contact..." />
                      )}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Label>Arrangement Start Date*</Label>
                <Controller
                  control={methods.control}
                  name="startDate"
                  render={({ field: { onChange, value = null } }) => (
                    <LocalizationProvider dateAdapter={DateAdapter}>
                      <DatePicker
                        value={value ?? null}
                        onChange={onChange}
                        {...(endDate && { maxDate: new Date(endDate) })}
                        renderInput={(params) => (
                          <TextField size="small" fullWidth {...params} required />
                        )}
                        inputFormat="dd/MM/yyyy"
                      />
                    </LocalizationProvider>
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Label>Arrangement End Date*</Label>
                <WsDatePicker
                  name="endDate"
                  label=""
                  {...(startDate && { minDate: new Date(startDate) })}
                  size="small"
                  required
                />
              </Grid>
              <Grid item xs={12} sm={12}>
                <Label>Service Fees*</Label>
                <Stack direction="row" spacing={1}>
                  <Controller
                    control={methods.control}
                    name="feeType"
                    render={({ field: { onChange, value } }) => (
                      <WsToggleButton
                        buttonGroupPadding={6}
                        buttonsList={arrangementType === 'PERMANENT' ? feeTypes : contractfeeTypes}
                        defaultValue={value ?? 'FIXED'}
                        selectedIndexValue={onChange}
                      />
                    )}
                  />
                  <Box sx={{ flexGrow: 1 }}>
                    {feeType === 'PERCENTAGE' && (
                      <Controller
                        control={methods.control}
                        name="fee"
                        render={({ field: { value, onChange }, fieldState }) => (
                          <Autocomplete
                            value={String(value)}
                            freeSolo
                            size="small"
                            selectOnFocus
                            handleHomeEndKeys
                            options={['5', '10']}
                            onInputChange={(event, value) => onChange(value)}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                required
                                label="Service Fees"
                                error={!!fieldState.error}
                                helperText={fieldState.error?.message}
                              />
                            )}
                          />
                        )}
                      />
                    )}
                    {feeType === 'FIXED' && (
                      <Stack direction="row" spacing={1}>
                        <WsSelect
                          placeholder="Currency"
                          name="currency"
                          required
                          selectArray={
                            currencies?.map((currency) => ({
                              label: currency?.symbol,
                              value: currency?.id,
                            })) ?? []
                          }
                          size="small"
                        />
                        <ControlledTextInput
                          fullWidth
                          name="fee"
                          required
                          placeholder="Service Fees*"
                          size="small"
                          inputProps={{}}
                        />
                        <WsSelect
                          required
                          placeholder="Period"
                          name="serviceFeePeriod"
                          selectArray={serviceFeePeriod}
                          size="small"
                        />
                      </Stack>
                    )}
                  </Box>
                </Stack>
              </Grid>
              <Grid item xs={12}>
                <ControlledTextArea name="comments" labelText="Comments" minRows={4} />
              </Grid>
            </Grid>
            {modalType === 'modal' ? (
              <Stack direction="row" justifyContent="flex-end" m={2} sx={{ mt: -10 }} spacing={2}>
                <Button size="large" color="error" onClick={onCancel}>
                  Cancel
                </Button>
                <WsButton size="large" endIcon={<ChevronRight />} type="submit">
                  Save
                </WsButton>
              </Stack>
            ) : (
              <DrawerFooter>
                <Stack direction="row" justifyContent="center" m={2} pr={6} spacing={2}>
                  <WsButton size="large" onClick={onCancel}>
                    Cancel
                  </WsButton>
                  <WsButton
                    size="large"
                    type="submit"
                    disabled={isLoading}
                    endIcon={
                      isLoading ? (
                        <CircularProgress size={17} color="secondary" />
                      ) : (
                        <ChevronRight />
                      )
                    }
                  >
                    {isLoading ? 'Saving...' : 'Save'}
                  </WsButton>
                </Stack>
              </DrawerFooter>
            )}
          </Box>
        </FormProvider>
      </ErrorBoundary>
    </>
  );
};

export const CurrencyDropdown = ({ name }: { name: string }) => {
  const [currencies, setCurrencies] = useState([]);

  useEffect(() => {
    (async () => {
      const response = await getCurrencyList();
      setCurrencies(
        response.data.map((currency) => ({
          label: currency?.symbol,
          value: currency?.id,
        }))
      );
    })();
  }, []);

  return <WsSelect placeholder="Currency" name={name} selectArray={currencies} size="small" />;
};

export default FeeArrangementForm;
